import { FC, ReactNode, useRef } from 'react';
import { useLockBodyScroll, useClickAway } from 'react-use';
import styled from 'styled-components';

import { mediumShadow } from 'ui/theme';
import { useFocusTrap } from 'utils/hooks/useFocusTrap';
import { useFirstFocusableElement } from 'utils/hooks/useFirstFocusableElement';
import { pxToRem } from 'utils/convert';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import { DrawerTitle } from './DrawerTitle';
import { DrawerFooter } from './DrawerFooter';
import { useDrawerContext } from './context';

const TRANSITION_DURATION = '250ms';
const DRAWER_WIDTH = pxToRem(976);
const DRAWER_OVERLAY_ID = 'drawerOverlayId';

const BackgroundWrapper = styled.div<{ $isExpanded: boolean; $navBarHeight: number }>`
  inset: 0;
  width: ${({ $isExpanded }) => ($isExpanded ? '100%' : '0px')};
  position: fixed;
  top: ${({ $navBarHeight, theme }) => theme.spacing.utils.pxToRem($navBarHeight)};
  background: ${({ theme }) => theme.colors.backgroundColors.backgroundScreen};
  z-index: ${({ theme }) => theme.styles.zIndex.drawerOverlay};
  ${({ $isExpanded }) => ($isExpanded ? mediumShadow : null)}
`;

const OuterWrapper = styled.div<{ $isExpanded: boolean; $navBarHeight: number }>`
  width: ${({ $isExpanded }) => ($isExpanded ? DRAWER_WIDTH : '0px')};
  transition: width ${TRANSITION_DURATION} ease;
  position: fixed;
  right: 0;
  top: ${({ $navBarHeight, theme }) => theme.spacing.utils.pxToRem($navBarHeight)};
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.backgroundColors.backgroundPrimary};
  border-color: #c3c3c5;
  border-style: solid;
  border-width: 0 0 0 ${({ theme }) => theme.spacing.values.single};
`;

const InnerWrapper = styled.div<{ $isExpanded: boolean }>`
  opacity: ${({ $isExpanded }) => ($isExpanded ? '1.0' : '0.0')};
  transition: opacity ${TRANSITION_DURATION} ease
    ${({ $isExpanded }) => ($isExpanded ? TRANSITION_DURATION : 0)};
  width: ${DRAWER_WIDTH};
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ChildrenWrapper = styled.div<{ $hasFooter: boolean }>`
  padding: ${({ theme }) => theme.spacing.values.plusHalf};
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  margin-bottom: ${({ $hasFooter, theme }) => theme.spacing.utils.pxToRem($hasFooter ? 53 : 0)};
`;

type DrawerPanelProps = {
  onClose: () => void;
  isExpanded: boolean;
  title: ReactNode;
  footer?: ReactNode;
  toolbar?: ReactNode;
};

export const DrawerPanel: FC<React.PropsWithChildren<DrawerPanelProps>> = ({
  children,
  onClose,
  isExpanded,
  title,
  footer,
  toolbar,
}) => {
  const { contentRef: ref } = useDrawerContext();
  const outerWrapperRef = useRef<HTMLDivElement | null>(null);
  const navBarHeight = useNavBarHeight();

  useLockBodyScroll(isExpanded);
  useClickAway(outerWrapperRef, ({ target }) => {
    // other popups may exist, like for instance, a toast. without this check,
    // clicking on a toast would close the drawer, which may not be an intended interaction.
    // so we only close the drawer, if a user clicks on the overlay.
    const isDrawerOverlay = target instanceof HTMLElement && target.id === DRAWER_OVERLAY_ID;
    if (isExpanded && isDrawerOverlay) onClose();
  });

  useFocusTrap(onClose, outerWrapperRef, !isExpanded, [isExpanded]);
  useFirstFocusableElement(ref, !isExpanded, [isExpanded, ref]);

  return (
    <BackgroundWrapper
      data-test="drawer--panel-background"
      $isExpanded={isExpanded}
      $navBarHeight={navBarHeight}
      id={DRAWER_OVERLAY_ID}
    >
      <OuterWrapper
        $isExpanded={isExpanded}
        data-test="drawer--panel"
        ref={outerWrapperRef}
        $navBarHeight={navBarHeight}
      >
        <InnerWrapper $isExpanded={isExpanded}>
          <DrawerTitle title={title} toolbar={toolbar} onClose={onClose} isExpanded={isExpanded} />
          <ChildrenWrapper $hasFooter={!!footer} ref={ref}>
            {children}
          </ChildrenWrapper>
          {!!footer && <DrawerFooter footer={footer} />}
        </InnerWrapper>
      </OuterWrapper>
    </BackgroundWrapper>
  );
};
